import React, { useState, useEffect, useCallback } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import { usePdfSettings } from '../../../layouts/PdfSettingsContext.js';
import BackButton from '../../../ui/BackButton';
import {
  Button, Container, FormControl, Box, Modal, MenuItem, Select,
  TextField, Typography, IconButton, useTheme, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, TablePagination, Chip, InputAdornment, Divider,
  Avatar, Tooltip, Checkbox, InputLabel
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';

// ─── Alert ───────────────────────────────────────────────────────────────────
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─── Section heading ──────────────────────────────────────────────────────────
const SectionLabel = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
    <Box
      sx={{
        width: 3,
        height: 18,
        borderRadius: 4,
        bgcolor: 'primary.main',
      }}
    />

    {icon &&
      React.cloneElement(icon, {
        sx: {
          fontSize: 15,
          color: 'primary.main',
        },
      })}

    <Typography
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: 'primary.main',
      }}
    >
      {text}
    </Typography>
  </Box>
);

// ─── Form row ─────────────────────────────────────────────────────────────────
const FormRow = ({ label, required, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      mb: 2,
    }}
  >
    <Box
      sx={{
        width: 160,
        minWidth: 160,
        pt: '9px',
        textAlign: 'right',
        pr: 2,
        fontSize: '0.82rem',
        fontWeight: 500,
        color: 'text.primary',
        lineHeight: 1.3,
      }}
    >
      {label}

      {required && (
        <span
          style={{
            color: '#e53935',
            marginLeft: 2,
          }}
        >
          *
        </span>
      )}
    </Box>

    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

// ─── Shared input sx ──────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiInputLabel-root': { color: 'text.primary' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
  '& .MuiInputBase-input': { color: 'text.primary' },
  '& .MuiInputBase-input::placeholder': { color: 'text.disabled', opacity: 1 },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: (theme) => theme.palette.text.disabled,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: 'text.disabled' },
  '& input[type="date"]': {
    colorScheme: (theme) => (theme.palette.mode === 'dark' ? 'dark' : 'light'),
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },

  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    fontSize: '0.875rem',
    height: 38,
    transition: 'box-shadow 0.2s',
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
  },
  '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
};

const selectSx = {
  borderRadius: '6px',
  fontSize: '0.875rem',
  height: 38,
  color: 'text.primary',
  transition: 'box-shadow 0.2s',
  '& .MuiSelect-icon': { color: 'text.primary' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
  '& .MuiSelect-select': { py: '8px', px: '10px' },
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 130,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: color + '18',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, {
        sx: {
          color,
          fontSize: 20,
        },
      })}
    </Box>

    <Box>
      <Typography
        sx={{
          fontSize: '0.68rem',
          color: 'text.primary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: '1.15rem',
          fontWeight: 700,
          color: 'text.primary',
          mt: 0.3,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ─── Financial year ───────────────────────────────────────────────────────────
const getFinancialYear = (dueDateStr) => {
  if (!dueDateStr) {
    const now = new Date();
    const y = now.getFullYear();

    return now.getMonth() >= 3
      ? `${y}-${y + 1}`
      : `${y - 1}-${y}`;
  }

  const d = new Date(dueDateStr);
  const y = d.getFullYear();

  return d.getMonth() >= 3
    ? `${y}-${y + 1}`
    : `${y - 1}-${y}`;
};

// ─── Month label ──────────────────────────────────────────────────────────────
const getMonthLabel = (dueDateStr, installmentNo) => {
  if (dueDateStr) {
    try {
      return new Date(dueDateStr).toLocaleString('en-IN', {
        month: 'long',
        year: 'numeric',
      });
    } catch {
      // fall through
    }
  }

  return `Installment ${installmentNo}`;
};

// ─── Receipt helpers ──────────────────────────────────────────────────────────
const DEFAULT_RECEIPT_PRIMARY = '#1A237E';
const DEFAULT_RECEIPT_ACCENT = '#FFFFFF';

const hexToRgbArray = (hex) => {
  const clean = String(hex || '#000000').replace('#', '');
  const full =
    clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean;
  return [0, 2, 4].map((i) => parseInt(full.substring(i, i + 2), 16));
};

const rgbToHex = (rgb) =>
  '#' +
  rgb
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')
    )
    .join('');

// amount 0..1 moves the colour toward white
const tintColor = (hex, amount) =>
  rgbToHex(hexToRgbArray(hex).map((v) => v + (255 - v) * amount));

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const numberToWords = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '';

  const rupees = Math.floor(Math.abs(number));
  const paise = Math.round((Math.abs(number) - rupees) * 100);

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy',
    'Eighty', 'Ninety',
  ];

  const two = (n) =>
    n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`;

  const three = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return `${h ? `${ones[h]} Hundred` : ''}${h && r ? ' ' : ''}${r ? two(r) : ''}`;
  };

  const convert = (n) => {
    if (n === 0) return 'Zero';
    let rem = n;
    const parts = [];
    const crore = Math.floor(rem / 10000000); rem %= 10000000;
    const lakh = Math.floor(rem / 100000); rem %= 100000;
    const thousand = Math.floor(rem / 1000); rem %= 1000;
    if (crore) parts.push(`${three(crore)} Crore`);
    if (lakh) parts.push(`${three(lakh)} Lakh`);
    if (thousand) parts.push(`${three(thousand)} Thousand`);
    if (rem) parts.push(three(rem));
    return parts.join(' ');
  };

  let words = `${convert(rupees)} Rupees`;
  if (paise) words += ` and ${convert(paise)} Paise`;
  return `${words} Only`;
};

const imageUrlToDataUrl = async (imageUrl) => {
  try {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:image/')) return imageUrl;
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error(`Failed: ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Unable to convert logo to data URL:', error);
    return null;
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// Fee Deposit
// ══════════════════════════════════════════════════════════════════════════════
const FeeDeposit = () => {
  const theme = useTheme();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  const [remarks, setRemarks] = useState('');

  const clientData = JSON.parse(
    localStorage.getItem('client') || 'null'
  );

  const { selectedBranchId } = useBranch();
const selectedBranch = selectedBranchId ? String(selectedBranchId) : '';

  // ── Snackbar ──────────────────────────────────────────────────────────────
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showSnack = (msg, sev = 'success') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setOpenSnackbar(true);
  };

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // ── Deposit history ───────────────────────────────────────────────────────
  const [depositHistoryModal, setDepositHistoryModal] = useState(false);
  const [depositHistoryInstallment, setDepositHistoryInstallment] =
    useState(null);

  // ── Fee generation list ───────────────────────────────────────────────────
  const [feeGenerations, setFeeGenerations] = useState([]);
  const [loadingFeeGenerations, setLoadingFeeGenerations] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');

  // ── Selected fee generation ───────────────────────────────────────────────
  const [selectedFeeGeneration, setSelectedFeeGeneration] =
    useState(null);

  const [candidateNameDisplay, setCandidateNameDisplay] =
    useState('');

  // ── Installments ───────────────────────────────────────────────────────────
  const [feeInstallmentData, setFeeInstallmentData] = useState(null);
  const [loadingInstallments, setLoadingInstallments] = useState(false);

  const allInstallments =
    feeInstallmentData?.installments || [];

  // ── Monthly / non-monthly ─────────────────────────────────────────────────
  const isMonthlyFee =
    (selectedFeeGeneration?.fee_type || '')
      .toLowerCase()
      .includes('month');

  // ── Payment form ──────────────────────────────────────────────────────────
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [loadingPaymentModes, setLoadingPaymentModes] =
    useState(true);

  const [bankName, setBankName] = useState('');
  const [referenceNo, setReferenceNo] = useState('');

  // ── Non-monthly ───────────────────────────────────────────────────────────
  const [selectedInstallmentId, setSelectedInstallmentId] =
    useState('');

  const [payableAmount, setPayableAmount] = useState('');

  // ── Monthly ────────────────────────────────────────────────────────────────
  const [checkedMonths, setCheckedMonths] = useState(new Set());
  const [monthPayAmounts, setMonthPayAmounts] = useState({});

  const [isSaving, setIsSaving] = useState(false);

  // ── Deposits ───────────────────────────────────────────────────────────────
  const [deposits, setDeposits] = useState([]);
  const [loadingDeposits, setLoadingDeposits] = useState(false);
  const [errorDeposits, setErrorDeposits] = useState(null);

  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Delete / Edit ─────────────────────────────────────────────────────────
  const [depositToDelete, setDepositToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editDeposit, setEditDeposit] = useState(null);
  const [editPaidAmount, setEditPaidAmount] = useState('');
  const [editPaymentMode, setEditPaymentMode] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState('');
  const [editBankName, setEditBankName] = useState('');
  const [editReferenceNo, setEditReferenceNo] = useState('');

    // ── Receipt (print) ───────────────────────────────────────────────────────
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptColorPrimary, setReceiptColorPrimary] = useState(
    pdfThemeColor || DEFAULT_RECEIPT_PRIMARY
  );
  const [receiptColorText, setReceiptColorText] = useState(pdfTextColor);
  const [receiptColorAccent, setReceiptColorAccent] = useState(
    DEFAULT_RECEIPT_ACCENT
  );

  useEffect(() => {
    if (receiptModalOpen) {
      setReceiptColorPrimary(pdfThemeColor || DEFAULT_RECEIPT_PRIMARY);
      setReceiptColorText(pdfTextColor);
    }
  }, [receiptModalOpen, pdfThemeColor, pdfTextColor]);
  const [receiptBranch, setReceiptBranch] = useState({
    name: '',
    branch: '',
    logo: '',
    address: '',
    phone: '',
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_TOKEN =
    'Bearer ' + localStorage.getItem('accessToken');

  // ── Organization ──────────────────────────────────────────────────────────
  const getOrganizationId = () => {
    let sessionUser = {};

    try {
      sessionUser =
        JSON.parse(sessionStorage.getItem('user')) || {};
    } catch (e) {
      // ignore
    }

    return (
      sessionUser?.organization_id ||
      sessionUser?.organization?.id ||
      clientData?.id
    );
  };

  // ── Payment mode helpers ──────────────────────────────────────────────────
  const getModeName = (modeId) =>
    paymentModes
      .find((m) => String(m.id) === String(modeId))
      ?.name?.toLowerCase() || '';

  const isBank = (modeId) =>
    getModeName(modeId).includes('bank');

  const isUpi = (modeId) =>
    getModeName(modeId).includes('upi');

  // ── Selected installment ──────────────────────────────────────────────────
  const selectedInstallment =
    allInstallments.find(
      (i) =>
        String(i.installment_id) ===
        String(selectedInstallmentId)
    ) || null;

  // ── Monthly total ─────────────────────────────────────────────────────────
  const monthlyTotalPayable =
    Array.from(checkedMonths).reduce((sum, id) => {
      return sum + Number(monthPayAmounts[id] || 0);
    }, 0);

  // ══════════════════════════════════════════════════════════════════════════
  // Initial payment date
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    setPaymentDate(
      new Date().toISOString().split('T')[0]
    );
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // Fee generation list
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedBranch) {
      setFeeGenerations([]);
      setSelectedFeeGeneration(null);
      setCandidateNameDisplay('');
      setLoadingFeeGenerations(false);
      return;
    }

    setLoadingFeeGenerations(true);

    const params = new URLSearchParams();
    const org = getOrganizationId();

    if (org) {
      params.append('organization', org);
    }

    if (selectedBranch) {
      params.append('branch', selectedBranch);
    }

    axios
      .get(
        `${API_BASE_URL}/api/fee_details/fee-generation/list/?${params.toString()}`,
        {
          headers: {
            Authorization: API_TOKEN,
          },
        }
      )
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || [];

        setFeeGenerations(data);
      })
      .catch(() =>
        showSnack(
          'Could not load fee generation list',
          'error'
        )
      )
      .finally(() =>
        setLoadingFeeGenerations(false)
      );
 }, [selectedBranch]);

  // ══════════════════════════════════════════════════════════════════════════
  // Payment methods
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    axios
      .get(
        `${API_BASE_URL}/api/master/payment-methods/`,
        {
          headers: {
            Authorization: API_TOKEN,
          },
        }
      )
      .then((res) => {
        setPaymentModes(
          Array.isArray(res.data)
            ? res.data
            : res.data?.results || []
        );
      })
      .catch(() =>
        showSnack(
          'Could not load payment methods',
          'error'
        )
      )
      .finally(() =>
        setLoadingPaymentModes(false)
      );
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // Load installments
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedFeeGeneration) {
      setFeeInstallmentData(null);
      setSelectedInstallmentId('');
      setPayableAmount('');
      setCheckedMonths(new Set());
      setMonthPayAmounts({});
      return;
    }

    setLoadingInstallments(true);

    axios
      .get(
        `${API_BASE_URL}/api/fee_details/fee-generation/${selectedFeeGeneration.fee_id}/installments-with-deposits/`,
        {
          headers: {
            Authorization: API_TOKEN,
          },
        }
      )
      .then((res) => {
        const data = res.data;

        setFeeInstallmentData(data);

        const installments = data?.installments || [];

        if (
          (selectedFeeGeneration?.fee_type || '')
            .toLowerCase()
            .includes('month')
        ) {
          // Monthly
          const initAmounts = {};

          installments.forEach((i) => {
            if (!i.is_paid) {
              initAmounts[i.installment_id] =
                Number(
                  i.remaining_amount || 0
                ).toFixed(2);
            }
          });

          setMonthPayAmounts(initAmounts);
          setCheckedMonths(new Set());
        } else {
          // ─────────────────────────────────────────────────────
          // Non-monthly: auto-select first unpaid installment
          // AND auto-fill its due date
          // ─────────────────────────────────────────────────────
          const firstPending =
            installments.find(
              (i) => !i.is_paid
            );

          if (firstPending) {
            setSelectedInstallmentId(
              firstPending.installment_id
            );

            const remainingAmount =
              Number(
                firstPending.remaining_amount || 0
              );

            setPayableAmount(
              remainingAmount > 0
                ? remainingAmount.toFixed(2)
                : '0.00'
            );

            // IMPORTANT:
            // Installment due_date becomes Payment Date
            if (firstPending.due_date) {
              setPaymentDate(
                String(
                  firstPending.due_date
                ).slice(0, 10)
              );
            }
          }
        }
      })
      .catch(() => {
        showSnack(
          'Could not load installment details',
          'error'
        );

        setFeeInstallmentData(null);
      })
      .finally(() =>
        setLoadingInstallments(false)
      );
  }, [selectedFeeGeneration]);

  // ══════════════════════════════════════════════════════════════════════════
  // Fetch deposits
  // ══════════════════════════════════════════════════════════════════════════
  const fetchDeposits = useCallback(() => {
    if (!selectedBranch) {
      setDeposits([]);
      setErrorDeposits(null);
      setLoadingDeposits(false);
      setPage(0);
      return;
    }

    setLoadingDeposits(true);
    setErrorDeposits(null);

    const params = new URLSearchParams();

    const org = getOrganizationId();

    if (org) {
      params.append('organization', org);
    }

    if (selectedBranch) {
      params.append('branch', selectedBranch);
    }

    axios
      .get(
        `${API_BASE_URL}/api/fee_details/fee-deposit/?${params.toString()}`,
        {
          headers: {
            Authorization: API_TOKEN,
          },
        }
      )
      .then((res) => {
        let list = [];

        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (Array.isArray(res.data?.data)) {
          list = res.data.data;
        } else if (
          Array.isArray(res.data?.results)
        ) {
          list = res.data.results;
        }

        setDeposits(list);
      })
      .catch(() =>
        setErrorDeposits(
          'Could not load deposits'
        )
      )
      .finally(() =>
        setLoadingDeposits(false)
      );
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      fetchDeposits();
    }
  }, [fetchDeposits]);

  // ══════════════════════════════════════════════════════════════════════════
  // Derived stats
  // ══════════════════════════════════════════════════════════════════════════
  const totalCollected = deposits.reduce(
    (sum, d) =>
      sum +
      Number(
        d.paid_amount ||
          d.amount ||
          0
      ),
    0
  );

  const todayStr =
    new Date()
      .toISOString()
      .split('T')[0];

  const todayCount = deposits.filter(
    (d) =>
      (
        d.payment_date ||
        d.date ||
        ''
      ).startsWith(todayStr)
  ).length;

  const filteredDeposits =
    deposits.filter((d) => {
      // Show Recent Payments only for the currently selected student.
      if (selectedFeeGeneration) {
        const selectedAdmission = String(
          selectedFeeGeneration.admission_no ||
            selectedFeeGeneration.admission_code ||
            ''
        ).trim().toLowerCase();

        const selectedStudentId = String(
          selectedFeeGeneration.student_id ||
            selectedFeeGeneration.candidate_id ||
            selectedFeeGeneration.student ||
            ''
        ).trim().toLowerCase();

        const selectedStudentName = String(
          selectedFeeGeneration.candidate_name ||
            selectedFeeGeneration.student_name ||
            ''
        ).trim().toLowerCase();

        const depositAdmission = String(
          d.admission_no ||
            d.admission_code ||
            ''
        ).trim().toLowerCase();

        const depositStudentId = String(
          d.student_id ||
            d.candidate_id ||
            d.student ||
            ''
        ).trim().toLowerCase();

        const depositStudentName = String(
          d.student_name ||
            d.candidate_name ||
            ''
        ).trim().toLowerCase();

        const sameStudent =
          (selectedAdmission &&
            depositAdmission === selectedAdmission) ||
          (selectedStudentId &&
            depositStudentId === selectedStudentId) ||
          (selectedStudentName &&
            depositStudentName === selectedStudentName);

        if (!sameStudent) {
          return false;
        }
      }

      if (!tableSearchTerm.trim()) {
        return true;
      }

      const t =
        tableSearchTerm
          .toLowerCase()
          .trim();

      return (
        (d.student_name || '')
          .toLowerCase()
          .includes(t) ||
        (
          d.admission_no ||
          d.admission_code ||
          ''
        )
          .toLowerCase()
          .includes(t) ||
        (
          d.payment_mode_name ||
          d.mode ||
          ''
        )
          .toLowerCase()
          .includes(t)
      );
    });

  // ══════════════════════════════════════════════════════════════════════════
  // Refresh installments
  // ══════════════════════════════════════════════════════════════════════════
  const refreshInstallments = () => {
    if (!selectedFeeGeneration) {
      return;
    }

    axios
      .get(
        `${API_BASE_URL}/api/fee_details/fee-generation/${selectedFeeGeneration.fee_id}/installments-with-deposits/`,
        {
          headers: {
            Authorization: API_TOKEN,
          },
        }
      )
      .then((r) =>
        setFeeInstallmentData(r.data)
      )
      .catch(() => {});
  };

  // ══════════════════════════════════════════════════════════════════════════
  // NON-MONTHLY INSTALLMENT CHANGE
  // ══════════════════════════════════════════════════════════════════════════
  const handleInstallmentChange = (
    instId
  ) => {
    setSelectedInstallmentId(
      instId
    );

    const inst =
      allInstallments.find(
        (item) =>
          String(
            item.installment_id
          ) ===
          String(instId)
      );

    if (!inst) {
      setPayableAmount('0.00');
      return;
    }

    // Auto-fill payable amount
    const remainingAmount =
      Number(
        inst.remaining_amount || 0
      );

    setPayableAmount(
      remainingAmount > 0
        ? remainingAmount.toFixed(2)
        : '0.00'
    );

    // ─────────────────────────────────────────────────────────
    // FIX:
    // Selected installment due_date -> Payment Date
    // ─────────────────────────────────────────────────────────
    if (inst.due_date) {
      setPaymentDate(
        String(
          inst.due_date
        ).slice(0, 10)
      );
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Monthly checkbox
  // ══════════════════════════════════════════════════════════════════════════
  const handleMonthCheck = (
    instId,
    checked
  ) => {
    setCheckedMonths(
      (prev) => {
        const next =
          new Set(prev);

        if (checked) {
          next.add(instId);

          // Also auto-fill payment date
          // from selected monthly installment
          const inst =
            allInstallments.find(
              (item) =>
                String(
                  item.installment_id
                ) ===
                String(instId)
            );

          if (
            inst?.due_date
          ) {
            setPaymentDate(
              String(
                inst.due_date
              ).slice(0, 10)
            );
          }
        } else {
          next.delete(instId);
        }

        return next;
      }
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Monthly pay amount
  // ══════════════════════════════════════════════════════════════════════════
  const handleMonthPayAmount = (
    instId,
    val,
    maxAllowed
  ) => {
    const numVal =
      Number(val);

    if (
      val !== '' &&
      numVal > maxAllowed
    ) {
      showSnack(
        `Amount cannot exceed ₹${maxAllowed.toLocaleString(
          'en-IN',
          {
            minimumFractionDigits: 2,
          }
        )}`,
        'error'
      );

      return;
    }

    setMonthPayAmounts(
      (prev) => ({
        ...prev,
        [instId]: val,
      })
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Deposit history
  // ══════════════════════════════════════════════════════════════════════════
  const handleViewDepositHistory = (
    inst,
    e
  ) => {
    e.stopPropagation();

    setDepositHistoryInstallment(
      inst
    );

    setDepositHistoryModal(
      true
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Monthly save
  // ══════════════════════════════════════════════════════════════════════════
  const handleSaveMonthly =
    async () => {
      if (!selectedFeeGeneration) {
        return showSnack(
          'Please select a student',
          'error'
        );
      }

      if (!paymentDate) {
        return showSnack(
          'Please select payment date',
          'error'
        );
      }

      if (!paymentMode) {
        return showSnack(
          'Please select payment mode',
          'error'
        );
      }

      if (
        checkedMonths.size === 0
      ) {
        return showSnack(
          'Please select at least one month to pay',
          'error'
        );
      }

      if (
        isBank(paymentMode) &&
        (!bankName.trim() ||
          !referenceNo.trim())
      ) {
        return showSnack(
          'Bank name and reference number are required',
          'error'
        );
      }

      if (
        isUpi(paymentMode) &&
        !referenceNo.trim()
      ) {
        return showSnack(
          'Reference / Transaction ID is required for UPI',
          'error'
        );
      }

      // Validate selected months
      for (const id of checkedMonths) {
        const amt =
          Number(
            monthPayAmounts[id] ||
              0
          );

        if (amt <= 0) {
          return showSnack(
            'Enter a valid amount for all selected months',
            'error'
          );
        }

        const inst =
          allInstallments.find(
            (i) =>
              String(
                i.installment_id
              ) ===
              String(id)
          );

        if (
          inst &&
          amt >
            Number(
              inst.remaining_amount ||
                0
            )
        ) {
          return showSnack(
            `Amount for ${getMonthLabel(
              inst.due_date,
              inst.installment_no
            )} exceeds remaining balance`,
            'error'
          );
        }
      }

      setIsSaving(true);

      try {
        const [firstId] =
          Array.from(
            checkedMonths
          );

        const payload = {
          installment:
            Number(firstId),

          paid_amount:
            monthlyTotalPayable,

          payment_date:
            paymentDate,

          payment_mode:
            Number(paymentMode),

          ...(remarks.trim() && {
            remarks:
              remarks.trim(),
          }),

          ...(
            (isBank(paymentMode) ||
              isUpi(paymentMode)) &&
            referenceNo.trim() && {
              reference_no:
                referenceNo.trim(),
            }
          ),

          ...(isBank(paymentMode) &&
            bankName.trim() && {
              bank_name:
                bankName.trim(),
            }),
        };

        const res =
          await axios.post(
            `${API_BASE_URL}/api/fee_details/fee-deposit/insert-update/`,
            payload,
            {
              headers: {
                Authorization:
                  API_TOKEN,
              },
            }
          );

        showSnack(
          res.data?.message ||
            `${checkedMonths.size} month(s) payment recorded successfully`
        );

        setShowSuccessModal(
          true
        );

        fetchDeposits();
        refreshInstallments();
        handleReset();
      } catch (err) {
        showSnack(
          err.response?.data
            ?.error ||
            err.response?.data
              ?.detail ||
            err.response?.data
              ?.message ||
            'Failed to record payment',
          'error'
        );
      } finally {
        setIsSaving(false);
      }
    };

  // ══════════════════════════════════════════════════════════════════════════
  // Non-monthly save
  // ══════════════════════════════════════════════════════════════════════════
  const handleSaveNonMonthly =
    async () => {
      if (!selectedFeeGeneration) {
        return showSnack(
          'Please select a student / fee generation',
          'error'
        );
      }

      if (!paymentDate) {
        return showSnack(
          'Please select payment date',
          'error'
        );
      }

      if (!paymentMode) {
        return showSnack(
          'Please select payment mode',
          'error'
        );
      }

      if (!selectedInstallmentId) {
        return showSnack(
          'Please select an installment',
          'error'
        );
      }

      if (
        !payableAmount ||
        Number(payableAmount) <= 0
      ) {
        return showSnack(
          'Enter a valid payable amount',
          'error'
        );
      }

      if (
        isBank(paymentMode) &&
        (!bankName.trim() ||
          !referenceNo.trim())
      ) {
        return showSnack(
          'Bank name and reference number are required',
          'error'
        );
      }

      if (
        isUpi(paymentMode) &&
        !referenceNo.trim()
      ) {
        return showSnack(
          'Reference / Transaction ID is required for UPI',
          'error'
        );
      }

      const payload = {
        installment:
          Number(
            selectedInstallmentId
          ),

        payment_date:
          paymentDate,

        payment_mode:
          Number(paymentMode),

        paid_amount:
          Number(payableAmount),

        ...(remarks.trim() && {
          remarks:
            remarks.trim(),
        }),

        ...(
          (isBank(paymentMode) ||
            isUpi(paymentMode)) &&
          referenceNo.trim() && {
            reference_no:
              referenceNo.trim(),
          }
        ),

        ...(isBank(paymentMode) &&
          bankName.trim() && {
            bank_name:
              bankName.trim(),
          }),
      };

      setIsSaving(true);

      try {
        const res =
          await axios.post(
            `${API_BASE_URL}/api/fee_details/fee-deposit/insert-update/`,
            payload,
            {
              headers: {
                Authorization:
                  API_TOKEN,
              },
            }
          );

        showSnack(
          res.data?.message ||
            'Payment recorded successfully'
        );

        setShowSuccessModal(
          true
        );

        fetchDeposits();
        refreshInstallments();
        handleReset();
      } catch (err) {
        showSnack(
          err.response?.data
            ?.error ||
            err.response?.data
              ?.detail ||
            err.response?.data
              ?.message ||
            'Failed to record payment',
          'error'
        );
      } finally {
        setIsSaving(false);
      }
    };

  // ══════════════════════════════════════════════════════════════════════════
  // Save
  // ══════════════════════════════════════════════════════════════════════════
  const handleSave = () => {
    if (isMonthlyFee) {
      handleSaveMonthly();
    } else {
      handleSaveNonMonthly();
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Reset
  // ══════════════════════════════════════════════════════════════════════════
  const handleReset = () => {
    setSelectedFeeGeneration(null);
    setCandidateNameDisplay('');
    setCandidateSearchTerm('');
    setFeeInstallmentData(null);
    setSelectedInstallmentId('');
    setPayableAmount('');
    setCheckedMonths(new Set());
    setMonthPayAmounts({});

    setPaymentDate(
      new Date()
        .toISOString()
        .split('T')[0]
    );

    setPaymentMode('');
    setBankName('');
    setReferenceNo('');
    setRemarks('');
  };

  useEffect(() => {
  handleReset();
}, [selectedBranch]);

  // ══════════════════════════════════════════════════════════════════════════
  // Delete
  // ══════════════════════════════════════════════════════════════════════════
  const confirmDelete =
    async () => {
      if (!depositToDelete) {
        return;
      }

      setIsDeleting(true);

      try {
        const res =
          await axios.delete(
            `${API_BASE_URL}/api/fee_details/fee-deposit/delete/${depositToDelete}/`,
            {
              headers: {
                Authorization:
                  API_TOKEN,
              },
            }
          );

        showSnack(
          res.data?.message ||
            'Deposit cancelled successfully'
        );

        setDeposits((prev) =>
          prev.filter(
            (item) =>
              String(item.id) !==
              String(depositToDelete)
          )
        );
        setPage(0);

        fetchDeposits();
        refreshInstallments();
      } catch (err) {
        showSnack(
          err.response?.data
            ?.detail ||
            'Failed to cancel deposit',
          'error'
        );
      } finally {
        setIsDeleting(false);
        setDeleteModalOpen(false);
        setDepositToDelete(null);
      }
    };

  // ══════════════════════════════════════════════════════════════════════════
  // Open edit
  // ══════════════════════════════════════════════════════════════════════════
  const handleOpenEdit = (
    dep
  ) => {
    setEditDeposit(dep);

    setEditPaidAmount(
      dep.paid_amount ||
        dep.amount ||
        ''
    );

    setEditPaymentDate(
      dep.payment_date ||
        dep.date ||
        new Date()
          .toISOString()
          .split('T')[0]
    );

    setEditPaymentMode(
      paymentModes.find(
        (m) =>
          m.name?.toLowerCase() ===
          (
            dep.payment_mode_name ||
            ''
          ).toLowerCase()
      )?.id || ''
    );

    setEditBankName(
      dep.bank_name || ''
    );

    setEditReferenceNo(
      dep.reference_no || ''
    );

    setEditModalOpen(true);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Update
  // ══════════════════════════════════════════════════════════════════════════
  const handleUpdate =
    async () => {
      if (
        !editPaidAmount ||
        !editPaymentMode ||
        !editPaymentDate
      ) {
        return showSnack(
          'Please fill all required fields',
          'error'
        );
      }

      const payload = {
        installment:
          editDeposit.installment,

        paid_amount:
          Number(editPaidAmount),

        payment_mode:
          Number(editPaymentMode),

        payment_date:
          editPaymentDate,

        ...(editBankName.trim() && {
          bank_name:
            editBankName.trim(),
        }),

        ...(editReferenceNo.trim() && {
          reference_no:
            editReferenceNo.trim(),
        }),
      };

      setIsSaving(true);

      try {
        const res =
          await axios.put(
            `${API_BASE_URL}/api/fee_details/fee-deposit/${editDeposit.id}/`,
            payload,
            {
              headers: {
                Authorization:
                  API_TOKEN,
              },
            }
          );

        showSnack(
          res.data?.message ||
            'Deposit updated successfully'
        );

        fetchDeposits();
        refreshInstallments();

        setEditModalOpen(false);
      } catch (err) {
        showSnack(
          err.response?.data
            ?.detail ||
            'Update failed',
          'error'
        );
      } finally {
        setIsSaving(false);
      }
    };

  // ══════════════════════════════════════════════════════════════════════════
  // Print receipt
  // ══════════════════════════════════════════════════════════════════════════
   // ══════════════════════════════════════════════════════════════════════════
  // Receipt: branch details (name / logo / address for the header)
  // ══════════════════════════════════════════════════════════════════════════
  const fetchReceiptBranch = async () => {
    const fallback = {
      name:
        clientData?.name ||
        clientData?.organization_name ||
        'Institute Name',
      branch: '',
      logo: '',
      address: '',
      phone: '',
    };

    setReceiptBranch(fallback);

    if (!selectedBranch) return;

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/master/branch/${selectedBranch}/`,
        { headers: { Authorization: API_TOKEN } }
      );

      const d = res.data?.data || res.data || {};
      const origin = String(API_BASE_URL || '')
        .replace(/\/api\/?$/, '')
        .replace(/\/$/, '');

      const raw = d.image || d.logo || d.branch_logo || '';
      let absolute = '';

      if (raw && typeof raw === 'string') {
        absolute = /^(data:image\/|blob:|https?:\/\/)/i.test(raw)
          ? raw
          : `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
      }

      // Embed as data URL so it prints reliably
      const logo = absolute
        ? (await imageUrlToDataUrl(absolute)) || absolute
        : '';

      setReceiptBranch({
        name:
          d.organization_name ||
          d.organization?.name ||
          fallback.name,
        branch: d.name || d.branch_name || '',
        logo,
        address: d.address || '',
        phone: d.phone || d.mobile_no || d.contact_no || '',
      });
    } catch (err) {
      console.error('Branch details error:', err);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Print receipt (step 1: open colour dialog + load receipt from API)
  // GET /fee_details/fee-deposit/generate-receipt/{deposit_id}/
  // ══════════════════════════════════════════════════════════════════════════
  const handlePrintReceipt = async (payment) => {
    const depositId = payment?.id || payment?.deposit_id;

    if (!depositId) {
      showSnack('Deposit ID not found', 'error');
      return;
    }

    setReceiptData(null);
    setReceiptModalOpen(true);
    setReceiptLoading(true);

    fetchReceiptBranch();

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/fee_details/fee-deposit/generate-receipt/${depositId}/`,
        { headers: { Authorization: API_TOKEN } }
      );

      setReceiptData(res.data?.data || res.data);
    } catch (err) {
      showSnack(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Could not load receipt',
        'error'
      );
      setReceiptModalOpen(false);
    } finally {
      setReceiptLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Print receipt (step 2: build A4 receipt and open the print window)
  // ══════════════════════════════════════════════════════════════════════════
  const printReceiptDocument = () => {
    if (!receiptData) {
      showSnack('Receipt data not available', 'error');
      return;
    }

    const r = receiptData;
    const student = r.student || {};
    const pay = r.payment || {};
    const courses = Array.isArray(r.course_fee_details)
      ? r.course_fee_details
      : [];

      const isAdvancePayment =
  r.isAdvancePayment === true ||
  String(r.isAdvancePayment).toLowerCase() === 'true';
  
    const esc = escapeHtml;

    const primary = receiptColorPrimary;
    const accent = receiptColorAccent;
    const textColor = receiptColorText;
    const primaryEnd = tintColor(primary, 0.2);
    const primaryBorderLight = tintColor(primary, 0.75);
    const primaryTint = tintColor(primary, 0.9);
    const accentTint = tintColor(accent, 0.8);
    const onPrimary = theme.palette.getContrastText(primary);

    const totalFee = courses.reduce((s, c) => s + Number(c.total_fee || 0), 0);
    const totalAdvance = courses.reduce(
      (s, c) => s + Number(c.advance_amount || 0),
      0
    );
    const totalBalance = courses.reduce(
      (s, c) => s + Number(c.balance_amount || 0),
      0
    );

       // Total fee - remaining balance - this payment = paid before this receipt
    const previouslyPaid = Math.max(
      0,
      totalFee -
        Number(pay.balance_remaining || 0) -
        Number(pay.paid_amount || 0)
    );

        // Amount still payable = total fee - advance - previously paid
    const payableAmount = Math.max(
      0,
      totalFee  - previouslyPaid
    );

    const logoHtml = receiptBranch.logo
      ? `<img class="logo" src="${receiptBranch.logo}" alt="Logo" />`
      : '';

    const contactLine = [receiptBranch.address, receiptBranch.phone]
      .filter(Boolean)
      .map(esc)
      .join(' &nbsp;|&nbsp; ');

    const infoRow = (label, value) => `
      <tr>
        <td class="info-label">${label}</td>
        <td class="info-value">${
          value === null || value === undefined || value === ''
            ? '—'
            : esc(value)
        }</td>
      </tr>`;

    const courseRows = courses.length
      ? courses
          .map(
            (c, i) => `
        <tr>
          <td class="center">${i + 1}</td>
          <td class="left">${esc(c.course_name || '—')}</td>
          <td class="left">${esc(c.fee_type || '—')}</td>
          <td class="amount">${formatMoney(c.course_fee)}</td>
          <td class="amount">${formatMoney(c.extra_amount)}</td>
          <td class="amount">${formatMoney(c.discount)}</td>
          <td class="amount">${formatMoney(c.gst_amount)}<div class="sub">(${Number(
              c.gst_percentage || 0
            )}%)</div></td>
          <td class="amount">${formatMoney(c.kit_charges)}</td>
          <td class="amount strong">${formatMoney(c.total_fee)}</td>
        </tr>`
          )
          .join('') + `
        <tr class="grand-total-row">
          <td colspan="8" class="right strong">Grand Total (All Courses)</td>
          <td class="amount strong">${formatMoney(totalFee)}</td>
        </tr>`
      : `<tr><td colspan="9" class="center">No course details available</td></tr>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Fee Receipt — ${esc(r.receipt_no ?? '')}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * {
      margin: 0; padding: 0; box-sizing: border-box;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    body {
      font-family: "Georgia", "Times New Roman", serif;
      color: ${textColor}; background: #fff; padding: 8px 4px;
    }
    .receipt {
      max-width: 800px; margin: 0 auto;
      border: 2px solid ${primary}; border-radius: 10px; overflow: hidden;
    }

    /* HEADER */
    .header {
      background: linear-gradient(135deg, ${primary} 0%, ${primaryEnd} 100%);
      color: ${onPrimary};
      padding: 20px 28px;
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 4px solid ${accent};
    }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand .logo {
      width: 64px; height: 64px; object-fit: contain;
      background: #fff; border-radius: 8px; padding: 4px;
    }
    .company {
      font-size: 20px; letter-spacing: 2px; font-weight: bold;
      text-transform: uppercase;
    }
    .company-sub {
      font-size: 11px; letter-spacing: 1.5px; opacity: .85;
      margin-top: 4px; text-transform: uppercase;
    }
    .company-contact { font-size: 10px; opacity: .8; margin-top: 3px; }
    .title { text-align: right; }
    .title h1 { font-size: 24px; letter-spacing: 4px; font-weight: bold; }
    .title .period { font-size: 12px; margin-top: 4px; opacity: .9; letter-spacing: 1px; }

    /* STATUS BAND */
    .status-band {
      display: flex; justify-content: space-between;
      padding: 8px 28px; font-size: 11px; letter-spacing: 1px;
      background: ${primaryTint}; border-bottom: 1px solid ${primaryBorderLight};
      color: ${textColor}; text-transform: uppercase;
    }
    .status-band b { color: ${primary}; }

    /* BODY */
    .body { padding: 20px 28px 8px; }
    .section-title {
      font-size: 11px; font-weight: bold; letter-spacing: 2px;
      text-transform: uppercase; color: ${textColor};
      border-bottom: 2px solid ${accent};
      padding-bottom: 4px; margin-bottom: 10px;
    }
    .info-grid { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .info-grid td { vertical-align: top; padding: 3px 0; font-size: 12.5px; }
    .info-grid table { width: 100%; border-collapse: collapse; }
    .info-label { color: ${textColor}; width: 36%; padding-right: 8px !important; }
    .info-value { font-weight: bold; color: ${textColor}; }

    /* COURSE TABLE */
    .fee-table {
      width: 100%; border-collapse: collapse; margin-bottom: 18px;
      border: 1px solid ${primaryBorderLight};
    }
    .fee-table thead th {
      background: ${primary}; color: ${onPrimary};
      font-size: 10px; letter-spacing: 1px; text-transform: uppercase;
      padding: 8px 6px; text-align: right;
    }
    .fee-table thead th.left { text-align: left; }
    .fee-table thead th.center { text-align: center; }
    .fee-table td {
      padding: 7px 6px; font-size: 11.5px;
      border-bottom: 1px solid #eceff1; text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .fee-table td.left { text-align: left; }
    .fee-table td.center { text-align: center; }
    .fee-table td.strong { font-weight: bold; }
    .fee-table .grand-total-row td {
      background: ${accentTint}; font-weight: 800;
      border-top: 2px solid ${accent}; border-bottom: none;
      padding-top: 9px; padding-bottom: 9px;
    }
    .fee-table .grand-total-row td.right { text-align: right; }
    .fee-table .sub { font-size: 9px; color: ${textColor}; }
    .fee-table tfoot td {
      background: ${accentTint}; font-weight: bold; font-size: 12px;
      border-top: 2px solid ${accent}; border-bottom: none;
    }

    /* PAYMENT + SUMMARY */
    .two-col { display: flex; gap: 14px; margin-bottom: 14px; }
    .box {
      flex: 1; border: 1px solid ${primaryBorderLight};
      border-radius: 6px; padding: 12px 14px;
    }
    .box .section-title { margin-bottom: 8px; }
    .sum-row {
      display: flex; justify-content: space-between;
      font-size: 12.5px; padding: 4px 0; border-bottom: 1px solid #eceff1;
    }
    .sum-row:last-child { border-bottom: none; }
    .sum-row.total {
      background: ${accentTint}; font-weight: bold;
      padding: 6px 8px; margin-top: 4px; border-top: 2px solid ${accent};
    }

        .sum-row.payable {
      background: ${accentTint}; font-weight: bold;
      padding: 6px 8px; margin: 4px 0;
      border-left: 4px solid ${accent};
      border-bottom: none;
    }

    /* AMOUNT RECEIVED */
    .net-band {
      display: flex; justify-content: space-between; align-items: center;
      background: linear-gradient(135deg, ${primary} 0%, ${primaryEnd} 100%);
      color: ${onPrimary}; padding: 12px 20px; border-radius: 8px;
      margin-bottom: 8px; border-left: 6px solid ${accent};
    }
    .net-label { font-size: 13px; letter-spacing: 2px; text-transform: uppercase; font-weight: bold; }
    .net-amount { font-size: 22px; font-weight: bold; }
    .words-line { font-size: 11px; font-style: italic; color: ${textColor}; margin-bottom: 20px; text-align: right; }

    /* FOOTER */
    .footer {
      display: flex; justify-content: space-between; align-items: flex-end;
      padding: 26px 28px 18px; border-top: 1px dashed #b0b0b0; margin: 0 28px;
    }
    .sign-line {
      width: 180px; border-top: 1px solid ${textColor}; text-align: center;
      font-size: 11px; padding-top: 5px; letter-spacing: 1px;
    }
    .note {
      text-align: center; font-size: 9.5px; color: ${textColor};
      padding-bottom: 14px; letter-spacing: .5px;
    }
  </style>
</head>
<body>
  <div class="receipt">

    <div class="header">
      <div class="brand">
        ${logoHtml}
        <div>
          <div class="company">${esc(receiptBranch.name || 'Institute Name')}</div>
          ${
            receiptBranch.branch
              ? `<div class="company-sub">${esc(receiptBranch.branch)}</div>`
              : ''
          }
          ${contactLine ? `<div class="company-contact">${contactLine}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <h1>FEE RECEIPT</h1>
        <div class="period">Receipt No: ${esc(r.receipt_no ?? '—')}</div>
      </div>
    </div>

    <div class="status-band">
      <div>Receipt No: <b>${esc(r.receipt_no ?? '—')}</b></div>
      <div>Date: <b>${esc(fmtDate(r.receipt_date))}</b></div>
      <div>Mode: <b>${esc(pay.payment_mode || '—')}</b></div>
      <div>Adm. No: <b>${esc(student.admission_no || '—')}</b></div>
    </div>

    <div class="body">

      <div class="section-title">Student Details</div>
      <table class="info-grid">
        <tr>
          <td style="width:50%; padding-right:20px;">
            <table>
              ${infoRow('Student Name', student.name)}
              ${infoRow('Admission No', student.admission_no)}
            </table>
          </td>
          <td style="width:50%;">
            <table>
              ${infoRow('Mobile No', student.mobile_no)}
              ${infoRow(
                'Course(s)',
                Array.isArray(student.courses) ? student.courses.join(', ') : ''
              )}
            </table>
          </td>
        </tr>
      </table>

      <div class="section-title">Course Fee Details (₹)</div>
      <table class="fee-table">
        <thead>
          <tr>
            <th class="center">#</th>
            <th class="left">Course</th>
            <th class="left">Fee Type</th>
            <th>Course Fee</th>
            <th>Extra</th>
            <th>Discount</th>
            <th>GST</th>
            <th>Kit</th>
            <th>Total Fee</th>
          </tr>
        </thead>
        <tbody>${courseRows}</tbody>
      </table>

      <div class="two-col">
        <div class="box">
          <div class="section-title">Payment Details</div>
          <table class="info-grid" style="margin-bottom:0;">
            ${infoRow('Installment No', pay.installment_no)}
            ${infoRow(
              'Installment Amt',
              pay.installment_amount != null
                ? `₹ ${formatMoney(pay.installment_amount)}`
                : ''
            )}
            ${infoRow('Payment Mode', pay.payment_mode)}
            ${infoRow('Reference No', pay.reference_no)}
            ${infoRow(
              'Reference Date',
              pay.reference_date ? fmtDate(pay.reference_date) : ''
            )}
            ${infoRow('Bank Name', pay.bank_name)}
            ${infoRow('Ledger', pay.ledger)}
          </table>
        </div>

        <div class="box">
          <div class="section-title">Fee Summary</div>
                   <div class="sum-row"><span>Total Fee</span><span>₹ ${formatMoney(totalFee)}</span></div>
${
  isAdvancePayment
    ? `<div class="sum-row"><span>Advance Payment</span><span>₹ ${formatMoney(totalAdvance)}</span></div>`
    : `<div class="sum-row"><span>Previously Paid Amount</span><span>₹ ${formatMoney(previouslyPaid)}</span></div>
       <div class="sum-row payable"><span>Payable Amount</span><span>₹ ${formatMoney(payableAmount)}</span></div>
       <div class="sum-row"><span>Paid Amount (Today)</span><span>₹ ${formatMoney(pay.paid_amount)}</span></div>
       <div class="sum-row"><span>Balance (Course)</span><span>₹ ${formatMoney(totalBalance)}</span></div>`
}
<div class="sum-row total"><span>Balance Remaining</span><span>₹ ${formatMoney(pay.balance_remaining)}</span></div>
        </div>
      </div>

      <div class="net-band">
        <div class="net-label">Amount Received</div>
        <div class="net-amount">₹ ${formatMoney(pay.paid_amount)}</div>
      </div>
      <div class="words-line">(In words: ${esc(numberToWords(pay.paid_amount))})</div>
    </div>

    <div class="footer">
      <div class="sign-line">Received By</div>
      <div class="sign-line">Authorized Signatory</div>
    </div>

    <div class="note">
      This is a computer-generated receipt and does not require a physical signature.
      Fees once paid are non-refundable.
    </div>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=1000');

    if (!printWindow) {
      showSnack(
        'Unable to open print window. Please allow pop-ups for this site.',
        'error'
      );
      return;
    }

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 350);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Format date
  // ══════════════════════════════════════════════════════════════════════════
  const fmtDate = (d) => {
    if (!d) {
      return '—';
    }

    try {
      const [
        yy,
        mm,
        dd,
      ] = d.split('-');

      return `${dd}-${mm}-${yy}`;
    } catch {
      return d;
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Modal helpers
  // ══════════════════════════════════════════════════════════════════════════
  const modalBox = (
    width
  ) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform:
      'translate(-50%, -50%)',
    width: {
      xs: '95%',
      sm: width || 420,
    },
    bgcolor:
      'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    overflow: 'hidden',
  });

  const modalHeader = {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    px: 3,
    py: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
  };

  const modeColor = (
    name = ''
  ) => {
    const n =
      name.toLowerCase();

    if (
      n.includes('cash')
    ) {
      return {
        color: 'success',
      };
    }

    if (
      n.includes('bank') ||
      n.includes('cheque')
    ) {
      return {
        color: 'info',
      };
    }

    if (
      n.includes('upi')
    ) {
      return {
        color: 'secondary',
      };
    }

    return {
      color: 'warning',
    };
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Save disabled
  // ══════════════════════════════════════════════════════════════════════════
  const isSaveDisabled =
    isMonthlyFee
      ? (
          !selectedFeeGeneration ||
          !paymentDate ||
          !paymentMode ||
          checkedMonths.size === 0 ||
          isSaving
        )
      : (
          !selectedFeeGeneration ||
          !paymentDate ||
          !paymentMode ||
          !selectedInstallmentId ||
          !payableAmount ||
          Number(payableAmount) <= 0 ||
          isSaving
        );

  // ══════════════════════════════════════════════════════════════════════════
  // Financial year
  // ══════════════════════════════════════════════════════════════════════════
  const financialYear =
    allInstallments.length > 0
      ? getFinancialYear(
          allInstallments[0]
            ?.due_date
        )
      : getFinancialYear(null);

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Container
      sx={{
        maxWidth:
          '100% !important',
        py: 3,
        px: {
          xs: 1,
          sm: 2,
        },
      }}
    >
      <BackButton />

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <StatCard
          icon={
            <ReceiptLongIcon />
          }
          label="Total Receipts"
          value={
            loadingDeposits
              ? '…'
              : deposits.length
          }
          color="#1976d2"
        />

        <StatCard
          icon={
            <AccountBalanceWalletIcon />
          }
          label="Total Collected"
          value={
            loadingDeposits
              ? '…'
              : `₹${totalCollected.toLocaleString(
                  'en-IN',
                  {
                    minimumFractionDigits: 2,
                  }
                )}`
          }
          color="#2e7d32"
        />

        <StatCard
          icon={
            <TrendingUpIcon />
          }
          label="Today's Payments"
          value={
            loadingDeposits
              ? '…'
              : todayCount
          }
          color="#ed6c02"
        />
      </Box>

      {/* Main Layout */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          flexWrap: {
            xs: 'wrap',
            lg: 'nowrap',
          },
          mb: 4,
        }}
      >
        {/* LEFT FORM */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor:
              'divider',
            borderRadius: 2,
            p: {
              xs: 2.5,
              sm: 3.5,
            },
            flex: '0 0 auto',
            width: {
              xs: '100%',
              lg: isMonthlyFee
                ? 460
                : 620,
            },
          }}
        >
          <SectionLabel
            icon={
              <PersonSearchIcon />
            }
            text="Student / Fee Generation"
          />

          {/* Candidate Name */}
          <FormRow
            label="Candidate Name"
            required
          >
            <TextField
              fullWidth
              size="small"
              value={
                candidateNameDisplay
              }
              placeholder="Click to search fee generation…"
              onClick={() =>
                setShowCandidateModal(
                  true
                )
              }
              InputProps={{
                readOnly: true,
                sx: {
                  cursor:
                    'pointer',
                },

                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={() =>
                        setShowCandidateModal(
                          true
                        )
                      }
                      sx={{
                        height: 28,
                        borderRadius:
                          '4px',
                        textTransform:
                          'none',
                        fontSize:
                          '0.75rem',
                        fontWeight: 600,
                        px: 1.5,
                        gap: 0.5,
                      }}
                    >
                      <SearchIcon
                        sx={{
                          fontSize: 14,
                        }}
                      />

                      Search
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...inputSx,

                '& .MuiOutlinedInput-root':
                  {
                    ...inputSx[
                      '& .MuiOutlinedInput-root'
                    ],

                    cursor:
                      'pointer',
                  },
              }}
            />
          </FormRow>

          {/* Student Info */}
          {selectedFeeGeneration && (
            <Box
              sx={{
                ml: '160px',
                mb: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor:
                  theme.palette
                    .action
                    .selected,
                border:
                  '1px solid',
                borderColor:
                  'primary.100',
                display: 'flex',
                gap: 2,
                flexWrap:
                  'wrap',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color:
                    'text.primary',
                }}
              >
                <strong>
                  Adm. No:
                </strong>{' '}
                {selectedFeeGeneration.admission_no ||
                  '—'}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color:
                    'text.primary',
                }}
              >
                <strong>
                  Mobile:
                </strong>{' '}
                {selectedFeeGeneration.mobile_no ||
                  '—'}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color:
                    'text.primary',
                }}
              >
                <strong>
                  Fee Type:
                </strong>{' '}
                {selectedFeeGeneration.fee_type ||
                  '—'}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color:
                    Number(
                      selectedFeeGeneration.pending_amount
                    ) > 0
                      ? 'error.main'
                      : 'success.main',
                  fontWeight: 700,
                }}
              >
                <strong>
                  Pending:
                </strong>{' '}
                ₹
                {Number(
                  selectedFeeGeneration.pending_amount ||
                    0
                ).toLocaleString(
                  'en-IN',
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </Typography>
            </Box>
          )}

          {/* Non Monthly Installment Details */}
          {!isMonthlyFee &&
            selectedFeeGeneration && (
              <>
                <Divider
                  sx={{
                    my: 2.5,
                  }}
                />

                <SectionLabel
                  icon={
                    <ReceiptLongIcon />
                  }
                  text="Installment Details"
                />

                <FormRow label="Total Installments">
                  {loadingInstallments ? (
                    <Box
                      sx={{
                        display:
                          'flex',
                        alignItems:
                          'center',
                        gap: 1,
                        height: 38,
                      }}
                    >
                      <CircularProgress
                        size={14}
                      />

                      <Typography
                        variant="caption"
                        color="text.disabled"
                      >
                        Loading…
                      </Typography>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      value={
                        feeInstallmentData
                          ? allInstallments.length
                          : '—'
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={inputSx}
                    />
                  )}
                </FormRow>

                <FormRow
                  label="Select Installment"
                  required
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={
                      selectedInstallmentId
                        ? (() => {
                            const inst =
                              allInstallments.find(
                                (i) =>
                                  String(
                                    i.installment_id
                                  ) ===
                                  String(
                                    selectedInstallmentId
                                  )
                              );

                            return inst
                              ? `Installment ${inst.installment_no}`
                              : '';
                          })()
                        : ''
                    }
                    placeholder={
                      loadingInstallments
                        ? 'Loading…'
                        : allInstallments.length ===
                            0
                          ? 'Select a fee generation first'
                          : '—'
                    }
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      ...inputSx,

                      '& .MuiOutlinedInput-root':
                        {
                          ...inputSx[
                            '& .MuiOutlinedInput-root'
                          ],

                          bgcolor:
                            theme.palette
                              .action
                              .hover,
                        },
                    }}
                  />
                </FormRow>
              </>
            )}

          <Divider
            sx={{
              my: 2.5,
            }}
          />

          <SectionLabel
            icon={
              <PaymentIcon />
            }
            text="Payment Details"
          />

          {/* Payment Date */}
          <FormRow
            label="Payment Date"
            required
          >
            <TextField
              fullWidth
              size="small"
              type="date"
              value={paymentDate}
              onChange={(e) =>
                setPaymentDate(
                  e.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon
                      sx={{
                        fontSize: 15,
                        color:
                          'text.disabled',
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </FormRow>

          {/* Payment Mode */}
          <FormRow
            label="Payment Mode"
            required
          >
            <FormControl
              fullWidth
              size="small"
            >
              <Select
                value={
                  paymentMode
                }
                displayEmpty
                onChange={(e) => {
                  setPaymentMode(
                    e.target.value
                  );

                  setBankName('');
                  setReferenceNo('');
                }}
                disabled={
                  loadingPaymentModes
                }
                sx={selectSx}
              >
                <MenuItem value="">
                  <em
                    style={{
                      color:
                        '#9e9e9e',
                    }}
                  >
                    — Select Mode —
                  </em>
                </MenuItem>

                {paymentModes.map(
                  (m) => (
                    <MenuItem
                      key={m.id}
                      value={m.id}
                    >
                      {m.name ||
                        m.method_name ||
                        m.payment_method ||
                        'Unknown'}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </FormRow>

          {/* Bank */}
          {paymentMode &&
            isBank(
              paymentMode
            ) && (
              <>
                <FormRow
                  label="Bank Name"
                  required
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={
                      bankName
                    }
                    onChange={(e) =>
                      setBankName(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter bank name"
                    sx={inputSx}
                  />
                </FormRow>

                <FormRow
                  label="Reference / Cheque No"
                  required
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={
                      referenceNo
                    }
                    onChange={(e) =>
                      setReferenceNo(
                        e.target
                          .value
                      )
                    }
                    placeholder="Cheque or reference number"
                    sx={inputSx}
                  />
                </FormRow>
              </>
            )}

          {/* UPI */}
          {paymentMode &&
            isUpi(
              paymentMode
            ) && (
              <FormRow
                label="Transaction ID"
                required
              >
                <TextField
                  fullWidth
                  size="small"
                  value={
                    referenceNo
                  }
                  onChange={(e) =>
                    setReferenceNo(
                      e.target
                        .value
                    )
                  }
                  placeholder="UPI transaction / reference ID"
                  sx={inputSx}
                />
              </FormRow>
            )}

          {/* Remarks */}
          <FormRow label="Remarks">
            <TextField
              fullWidth
              size="small"
              value={remarks}
              onChange={(e) =>
                setRemarks(
                  e.target.value
                )
              }
              placeholder="Optional note"
              sx={inputSx}
            />
          </FormRow>

          {/* Non Monthly Amount */}
          {!isMonthlyFee &&
            selectedFeeGeneration && (
              <FormRow
                label="Payable Amount"
                required
              >
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={
                    payableAmount
                  }
                  onChange={(e) =>
                    setPayableAmount(
                      e.target.value
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.primary',
                            fontWeight: 700,
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </FormRow>
            )}

          {/* Monthly Total */}
          {isMonthlyFee &&
            checkedMonths.size >
              0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor:
                    'primary.50',
                  border:
                    '1px solid',
                  borderColor:
                    'primary.200',
                  display:
                    'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="primary.main"
                >
                  {
                    checkedMonths.size
                  }{' '}
                  month(s)
                  selected
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="primary.main"
                >
                  Total: ₹
                  {monthlyTotalPayable.toLocaleString(
                    'en-IN',
                    {
                      minimumFractionDigits: 2,
                    }
                  )}
                </Typography>
              </Box>
            )}

          {/* Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'flex-end',
              gap: 1.5,
              mt: 3,
              pt: 2,
              borderTop:
                '1px solid',
              borderColor:
                'divider',
            }}
          >
            <Button
              variant="outlined"
              onClick={
                handleReset
              }
              sx={{
                borderRadius:
                  '6px',
                textTransform:
                  'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              disableElevation
              onClick={
                handleSave
              }
              disabled={
                isSaveDisabled
              }
              sx={{
                borderRadius:
                  '6px',
                textTransform:
                  'none',
                fontWeight: 700,
                px: 4,
                fontSize:
                  '0.9rem',
                background:
                  'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              }}
            >
              {isSaving ? (
                <>
                  <CircularProgress
                    size={14}
                    color="inherit"
                    sx={{
                      mr: 1,
                    }}
                  />
                  Saving…
                </>
              ) : (
                <>
                  <SaveIcon
                    sx={{
                      fontSize: 16,
                      mr: 0.8,
                    }}
                  />
                  Save Payment
                </>
              )}
            </Button>
          </Box>
        </Paper>

        {/* RIGHT PANEL */}
        {selectedFeeGeneration && (
          <Box
            sx={{
              flex: 1,
              minWidth: 280,
              display: 'flex',
              flexDirection:
                'column',
              gap: 2,
            }}
          >
            {loadingInstallments ? (
              <Paper
                elevation={0}
                sx={{
                  border:
                    '1px solid',
                  borderColor:
                    'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign:
                    'center',
                }}
              >
                <CircularProgress
                  size={24}
                />
              </Paper>
            ) : isMonthlyFee ? (
              /* MONTHLY */
              <Paper
                elevation={0}
                sx={{
                  border:
                    '1px solid',
                  borderColor:
                    'divider',
                  borderRadius: 2,
                  overflow:
                    'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom:
                      '2px solid',
                    borderColor:
                      'primary.main',
                    display:
                      'flex',
                    alignItems:
                      'center',
                    justifyContent:
                      'space-between',
                    flexWrap:
                      'wrap',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display:
                        'flex',
                      alignItems:
                        'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      Financial Year:
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color:
                          'success.main',
                        fontWeight: 800,
                        fontSize:
                          '1.05rem',
                      }}
                    >
                      {
                        financialYear
                      }
                    </Typography>
                  </Box>

                  {feeInstallmentData && (
                    <Box
                      sx={{
                        display:
                          'flex',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.disabled"
                      >
                        Total:{' '}
                        <strong>
                          ₹
                          {Number(
                            feeInstallmentData.total_fee ||
                              0
                          ).toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                            }
                          )}
                        </strong>
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            'error.main',
                          fontWeight: 700,
                        }}
                      >
                        Balance: ₹
                        {Number(
                          feeInstallmentData.balance_amount ||
                            0
                        ).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                          }
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    display:
                      'flex',
                    alignItems:
                      'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    Select Months to Pay:
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.disabled"
                  >
                    (All selected
                    months will be
                    submitted in a
                    single
                    transaction)
                  </Typography>
                </Box>

                <TableContainer>
                  <Table
                    size="small"
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor:
                            theme
                              .palette
                              .background
                              .default,
                        }}
                      >
                        <TableCell>
                          S.No
                        </TableCell>
                        <TableCell>
                          Select
                        </TableCell>

                        <TableCell>
                          Month
                        </TableCell>

                        <TableCell align="right">
                          Monthly Fee
                        </TableCell>

                        <TableCell align="right">
                          Paid
                        </TableCell>

                        <TableCell align="right">
                          Pending
                        </TableCell>

                        <TableCell align="center">
                          Status
                        </TableCell>

                        <TableCell align="center">
                          History
                        </TableCell>

                        <TableCell align="right">
                          Pay Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {allInstallments.length ===
                      0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            align="center"
                            sx={{
                              py: 4,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.disabled"
                            >
                              No
                              installments
                              found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        allInstallments.map(
                          (inst, index) => {
                            const isPaid =
                              inst.is_paid;

                            const isChecked =
                              checkedMonths.has(
                                inst.installment_id
                              );

                            const totalPaid =
                              Number(
                                inst.total_paid ||
                                  0
                              );

                            const pending =
                              Number(
                                inst.remaining_amount ||
                                  0
                              );

                            const monthlyAmt =
                              Number(
                                inst.amount ||
                                  0
                              );

                            const isOverdue =
                              inst.due_date &&
                              new Date(
                                inst.due_date
                              ) <
                                new Date() &&
                              !isPaid;

                            const hasDeposits =
                              Array.isArray(
                                inst.deposits
                              ) &&
                              inst.deposits
                                .length >
                                0;

                            return (
                              <TableRow
                                key={
                                  inst.installment_id
                                }
                              >
                                <TableCell>
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    size="small"
                                    checked={
                                      isChecked
                                    }
                                    disabled={
                                      isPaid
                                    }
                                    onChange={(
                                      e
                                    ) =>
                                      handleMonthCheck(
                                        inst.installment_id,
                                        e
                                          .target
                                          .checked
                                      )
                                    }
                                  />
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize:
                                        '0.85rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getMonthLabel(
                                      inst.due_date,
                                      inst.installment_no
                                    )}
                                  </Typography>

                                  {isOverdue && (
                                    <Typography
                                      variant="caption"
                                      color="error.main"
                                    >
                                      Overdue
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell align="right">
                                  {monthlyAmt.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {totalPaid.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {pending.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  <Chip
                                    label={
                                      isPaid
                                        ? 'Paid'
                                        : 'Pending'
                                    }
                                    size="small"
                                    color={
                                      isPaid
                                        ? 'success'
                                        : 'error'
                                    }
                                    sx={{
                                      height: 22,
                                      fontSize:
                                        '0.72rem',
                                    }}
                                  />
                                </TableCell>

                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    disabled={
                                      !hasDeposits
                                    }
                                    onClick={(
                                      e
                                    ) =>
                                      handleViewDepositHistory(
                                        inst,
                                        e
                                      )
                                    }
                                  >
                                    <HistoryIcon
                                      sx={{
                                        fontSize: 16,
                                      }}
                                    />
                                  </IconButton>
                                </TableCell>

                                <TableCell align="right">
                                  {isPaid ? (
                                    <TextField
                                      size="small"
                                      value="0.00"
                                      disabled
                                    />
                                  ) : (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={
                                        monthPayAmounts[
                                          inst
                                            .installment_id
                                        ] ??
                                        ''
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleMonthPayAmount(
                                          inst.installment_id,
                                          e.target.value,
                                          pending
                                        )
                                      }
                                      onFocus={() => {
                                        if (
                                          !isChecked
                                        ) {
                                          handleMonthCheck(
                                            inst.installment_id,
                                            true
                                          );
                                        }
                                      }}
                                      inputProps={{
                                        step:
                                          '0.01',
                                        min: 0,
                                        max: pending,
                                      }}
                                      sx={{
                                        width: 120,
                                      }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {allInstallments.length >
                  0 && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop:
                        '2px solid',
                      borderColor:
                        'divider',
                      display:
                        'flex',
                      justifyContent:
                        'flex-end',
                      gap: 3,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.disabled"
                    >
                      Paid months:{' '}
                      <strong>
                        {
                          allInstallments.filter(
                            (i) =>
                              i.is_paid
                          ).length
                        }
                      </strong>
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.disabled"
                    >
                      Pending months:{' '}
                      <strong>
                        {
                          allInstallments.filter(
                            (i) =>
                              !i.is_paid
                          ).length
                        }
                      </strong>
                    </Typography>

                    {checkedMonths.size >
                      0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            'primary.main',
                          fontWeight: 700,
                        }}
                      >
                        Paying now: ₹
                        {monthlyTotalPayable.toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                          }
                        )}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            ) : (
              /* NON MONTHLY */
              <>
                {feeInstallmentData && (
                  <>
                    {/* Summary */}
                    <Paper
                      elevation={0}
                      sx={{
                        border:
                          '1px solid',
                        borderColor:
                          'divider',
                        borderRadius: 2,
                        overflow:
                          'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          background:
                            'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                          p: 2,
                          display:
                            'flex',
                          alignItems:
                            'center',
                          gap: 1.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor:
                              'rgba(255,255,255,0.2)',
                            width: 44,
                            height: 44,
                          }}
                        >
                          {(
                            feeInstallmentData.candidate_name ||
                            'S'
                          )[0].toUpperCase()}
                        </Avatar>

                        <Box>
                          <Typography
                            sx={{
                              color:
                                '#fff',
                              fontWeight: 700,
                            }}
                          >
                            {
                              feeInstallmentData.candidate_name
                            }
                          </Typography>

                          <Typography
                            sx={{
                              color:
                                'rgba(255,255,255,0.75)',
                              fontSize:
                                '0.78rem',
                            }}
                          >
                            {
                              selectedFeeGeneration.admission_no
                            }
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display:
                              'flex',
                            justifyContent:
                              'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">
                            Total Fee
                          </Typography>

                          <Typography
                            variant="body2"
                            fontWeight={700}
                          >
                            ₹
                            {Number(
                              feeInstallmentData.total_fee ||
                                0
                            ).toLocaleString(
                              'en-IN',
                              {
                                minimumFractionDigits: 2,
                              }
                            )}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display:
                              'flex',
                            justifyContent:
                              'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">
                            Paid
                          </Typography>

                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="success.main"
                          >
                            ₹
                            {Number(
                              feeInstallmentData.total_paid ||
                                0
                            ).toLocaleString(
                              'en-IN',
                              {
                                minimumFractionDigits: 2,
                              }
                            )}
                          </Typography>
                        </Box>

                        <Divider
                          sx={{
                            my: 1,
                          }}
                        />

                        <Box
                          sx={{
                            display:
                              'flex',
                            justifyContent:
                              'space-between',
                          }}
                        >
                          <Typography variant="body2">
                            Balance Due
                          </Typography>

                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={
                              Number(
                                feeInstallmentData.balance_amount
                              ) > 0
                                ? 'error.main'
                                : 'success.main'
                            }
                          >
                            ₹
                            {Number(
                              feeInstallmentData.balance_amount ||
                                0
                            ).toLocaleString(
                              'en-IN',
                              {
                                minimumFractionDigits: 2,
                              }
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Installment Schedule */}
                    <Paper
                      elevation={0}
                      sx={{
                        border:
                          '1px solid',
                        borderColor:
                          'divider',
                        borderRadius: 2,
                        overflow:
                          'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          bgcolor:
                            'background.default',
                          borderBottom:
                            '1px solid',
                          borderColor:
                            'divider',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize:
                              '0.75rem',
                            fontWeight: 700,
                            textTransform:
                              'uppercase',
                            letterSpacing:
                              0.5,
                          }}
                        >
                          Installment Schedule
                        </Typography>
                      </Box>

                      {allInstallments.map(
                        (inst) => {
                          const isSelected =
                            String(
                              selectedInstallmentId
                            ) ===
                            String(
                              inst.installment_id
                            );

                          const isPaid =
                            inst.is_paid;

                          const isPartial =
                            !isPaid &&
                            Number(
                              inst.total_paid ||
                                0
                            ) > 0;

                          const isOverdue =
                            inst.due_date &&
                            new Date(
                              inst.due_date
                            ) <
                              new Date() &&
                            !isPaid;

                          const hasDeposits =
                            Array.isArray(
                              inst.deposits
                            ) &&
                            inst.deposits
                              .length >
                              0;

                          return (
                            <Box
                              key={
                                inst.installment_id
                              }
                              onClick={() =>
                                !isPaid &&
                                handleInstallmentChange(
                                  inst.installment_id
                                )
                              }
                              sx={{
                                px: 2,
                                py: 1.5,
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                justifyContent:
                                  'space-between',
                                borderBottom:
                                  '1px solid',
                                borderColor:
                                  'divider',
                                cursor:
                                  isPaid
                                    ? 'default'
                                    : 'pointer',
                                bgcolor:
                                  isPaid
                                    ? 'rgba(46,125,50,0.05)'
                                    : isSelected
                                      ? 'primary.50'
                                      : 'transparent',
                                borderLeft:
                                  '3px solid',
                                borderLeftColor:
                                  isPaid
                                    ? '#2e7d32'
                                    : isSelected
                                      ? 'primary.main'
                                      : 'transparent',
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display:
                                      'flex',
                                    alignItems:
                                      'center',
                                    gap: 0.8,
                                    mb: 0.2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize:
                                        '0.82rem',
                                      fontWeight: 600,
                                      color:
                                        isPaid
                                          ? 'success.main'
                                          : isSelected
                                            ? 'primary.main'
                                            : 'text.primary',
                                    }}
                                  >
                                    Installment{' '}
                                    {
                                      inst.installment_no
                                    }
                                  </Typography>

                                  {isPaid && (
                                    <Chip
                                      label="PAID"
                                      size="small"
                                      color="success"
                                      sx={{
                                        height: 18,
                                        fontSize:
                                          '0.62rem',
                                      }}
                                    />
                                  )}

                                  {isPartial && (
                                    <Chip
                                      label="PARTIAL"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{
                                        height: 18,
                                        fontSize:
                                          '0.62rem',
                                      }}
                                    />
                                  )}
                                </Box>

                                {inst.due_date && (
                                  <Typography
                                    variant="caption"
                                    color={
                                      isOverdue
                                        ? 'error.main'
                                        : 'text.disabled'
                                    }
                                  >
                                    {isOverdue && (
                                      <WarningAmberIcon
                                        sx={{
                                          fontSize: 11,
                                          mr: 0.3,
                                          verticalAlign:
                                            'middle',
                                        }}
                                      />
                                    )}

                                    Due:{' '}
                                    {fmtDate(
                                      inst.due_date
                                    )}
                                  </Typography>
                                )}

                                {isPartial && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    Paid ₹
                                    {Number(
                                      inst.total_paid
                                    ).toLocaleString(
                                      'en-IN',
                                      {
                                        minimumFractionDigits: 2,
                                      }
                                    )}
                                    {' · '}
                                    ₹
                                    {Number(
                                      inst.remaining_amount
                                    ).toLocaleString(
                                      'en-IN',
                                      {
                                        minimumFractionDigits: 2,
                                      }
                                    )}{' '}
                                    left
                                  </Typography>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display:
                                    'flex',
                                  alignItems:
                                    'center',
                                  gap: 1,
                                  ml: 1,
                                }}
                              >
                                {hasDeposits && (
                                  <IconButton
                                    size="small"
                                    onClick={(
                                      e
                                    ) =>
                                      handleViewDepositHistory(
                                        inst,
                                        e
                                      )
                                    }
                                  >
                                    <HistoryIcon
                                      sx={{
                                        fontSize: 15,
                                      }}
                                    />
                                  </IconButton>
                                )}

                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize:
                                      '0.88rem',
                                    color:
                                      isPaid
                                        ? 'success.main'
                                        : isSelected
                                          ? 'primary.main'
                                          : 'text.primary',
                                  }}
                                >
                                  ₹
                                  {Number(
                                    isPartial
                                      ? inst.remaining_amount
                                      : inst.amount ||
                                          0
                                  ).toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                      )}
                    </Paper>
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Recent Payments */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            'space-between',
          mb: 1.5,
          flexWrap:
            'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
          >
            Recent Payments
          </Typography>

          {!loadingDeposits && (
            <Typography
              variant="caption"
              color="text.disabled"
            >
              {
                filteredDeposits.length
              }{' '}
              record
              {filteredDeposits.length !==
              1
                ? 's'
                : ''}{' '}
              total
            </Typography>
          )}
        </Box>

        <TextField
          size="small"
          placeholder="Search payments…"
          value={
            tableSearchTerm
          }
          onChange={(e) => {
            setTableSearchTerm(
              e.target.value
            );

            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    fontSize: 17,
                    color:
                      'text.disabled',
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            ...inputSx,
            width: 240,
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          border:
            '1px solid',
          borderColor:
            'divider',
          borderRadius: 2,
          overflow:
            'hidden',
        }}
      >
        <TableContainer>
          <Table
            stickyHeader
            size="small"
          >
            <TableHead>
              <TableRow>
                {[
                  '#',
                  'Admission No',
                  'Student Name',
                  'Date',
                  'Mode',
                  'Installment',
                  'Amount',
                  'Actions',
                ].map(
                  (h, i) => (
                    <TableCell
                      key={h}
                      align={
                        i === 7
                          ? 'center'
                          : 'left'
                      }
                      sx={{
                        bgcolor:
                          theme
                            .palette
                            .background
                            .default,
                        fontWeight: 700,
                        fontSize:
                          '0.75rem',
                        textTransform:
                          'uppercase',
                        letterSpacing:
                          0.5,
                        color:
                          'primary.main',
                        whiteSpace:
                          'nowrap',
                        py: 1.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loadingDeposits ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{
                      py: 6,
                    }}
                  >
                    <CircularProgress
                      size={28}
                    />

                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{
                        mt: 1,
                      }}
                    >
                      Loading
                      payments…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : errorDeposits ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{
                      py: 4,
                    }}
                  >
                    <MuiAlert severity="error">
                      {
                        errorDeposits
                      }
                    </MuiAlert>
                  </TableCell>
                </TableRow>
              ) : filteredDeposits.length ===
                0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{
                      py: 6,
                    }}
                  >
                    <ReceiptLongIcon
                      sx={{
                        fontSize: 40,
                        color:
                          'text.disabled',
                        mb: 1,
                      }}
                    />

                    <Typography
                      color="text.disabled"
                      variant="body2"
                    >
                      No payments
                      recorded
                      yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDeposits
                  .slice(
                    page *
                      rowsPerPage,
                    page *
                      rowsPerPage +
                      rowsPerPage
                  )
                  .map(
                    (
                      payment,
                      index
                    ) => {
                      const mc =
                        modeColor(
                          payment.payment_mode_name ||
                            payment.mode
                        );

                      return (
                        <TableRow
                          key={
                            payment.id
                          }
                          hover
                        >
                          <TableCell>
                            {page *
                              rowsPerPage +
                              index +
                              1}
                          </TableCell>

                          <TableCell>
                            {payment.admission_no ||
                              payment.admission_code ||
                              '—'}
                          </TableCell>

                          <TableCell>
                            {
                              payment.student_name ||
                              '—'
                            }
                          </TableCell>

                          <TableCell>
                            {fmtDate(
                              payment.payment_date ||
                                payment.date
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={
                                payment.payment_mode_name ||
                                payment.mode ||
                                '—'
                              }
                              size="small"
                              color={
                                mc.color
                              }
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            {payment.installment_no
                              ? `Installment ${payment.installment_no}`
                              : payment.installment
                                ? `#${payment.installment}`
                                : '—'}
                          </TableCell>

                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color:
                                  'success.main',
                              }}
                            >
                              ₹
                              {Number(
                                payment.paid_amount ||
                                  payment.amount ||
                                  0
                              ).toLocaleString(
                                'en-IN',
                                {
                                  minimumFractionDigits: 2,
                                }
                              )}
                            </Typography>
                          </TableCell>

<TableCell align="center">
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    {/* Print */}
    <Tooltip title="Print Receipt" arrow>
      <IconButton
        size="small"
        onClick={() =>
          handlePrintReceipt(payment)
        }
        sx={{
          color: 'info.main',
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'info.dark',
            bgcolor: 'rgba(33, 150, 243, 0.10)',
          },
        }}
      >
        <PrintIcon
          sx={{
            fontSize: 18,
          }}
        />
      </IconButton>
    </Tooltip>

    {/* Edit */}
    <Tooltip title="Edit Payment" arrow>
      <IconButton
        size="small"
        onClick={() =>
          handleOpenEdit(payment)
        }
        sx={{
          color: 'warning.main',
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'warning.dark',
            bgcolor: 'rgba(255, 152, 0, 0.10)',
          },
        }}
      >
        <EditIcon
          sx={{
            fontSize: 18,
          }}
        />
      </IconButton>
    </Tooltip>

    {/* Delete */}
    <Tooltip title="Delete Payment" arrow>
      <IconButton
        size="small"
        onClick={() => {
          setDepositToDelete(payment.id);
          setDeleteModalOpen(true);
        }}
        sx={{
          color: 'error.main',
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'error.dark',
            bgcolor: 'rgba(211, 47, 47, 0.10)',
          },
        }}
      >
        <DeleteIcon
          sx={{
            fontSize: 18,
          }}
        />
      </IconButton>
    </Tooltip>
  </Box>
</TableCell>


                        </TableRow>
                      );
                    }
                  )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loadingDeposits &&
          !errorDeposits &&
          filteredDeposits.length >
            0 && (
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                50,
              ]}
              component="div"
              count={
                filteredDeposits.length
              }
              rowsPerPage={
                rowsPerPage
              }
              page={page}
              onPageChange={(
                _,
                newPage
              ) =>
                setPage(
                  newPage
                )
              }
              onRowsPerPageChange={(
                e
              ) => {
                setRowsPerPage(
                  parseInt(
                    e.target.value,
                    10
                  )
                );

                setPage(0);
              }}
            />
          )}
      </Paper>

      {/* Candidate Modal */}
      <Modal
        open={
          showCandidateModal
        }
        onClose={() =>
          setShowCandidateModal(
            false
          )
        }
      >
        <Box
          sx={{
            ...modalBox(
              980
            ),
            width: {
              xs: '95%',
              sm: '92%',
              md: 980,
            },
            maxHeight:
              '88vh',
            display:
              'flex',
            flexDirection:
              'column',
          }}
        >
          <Box sx={modalHeader}>
            <Box
              sx={{
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 1.5,
              }}
            >
              <PersonSearchIcon
                sx={{
                  color: '#fff',
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Select Fee
                Generation
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setShowCandidateModal(
                  false
                )
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 2,
              borderBottom:
                `1px solid ${theme.palette.divider}`,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by admission no, name or fee type…"
              value={
                candidateSearchTerm
              }
              onChange={(e) =>
                setCandidateSearchTerm(
                  e.target.value
                )
              }
              autoFocus
              sx={inputSx}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY:
                'auto',
              p: 2,
            }}
          >
            <TableContainer>
              <Table
                stickyHeader
                size="small"
              >
                <TableHead>
                  <TableRow>
                    {[
                      'S.No',
                      'Admission No',
                      'Candidate Name',
                      'Fee Type',
                      'Total Fee',
                      'Paid',
                      'Pending',
                      'Action',
                    ].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              theme
                                .palette
                                .background
                                .default,
                          }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingFeeGenerations ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                      >
                        <CircularProgress
                          size={24}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    (() => {
                      const term =
                        candidateSearchTerm
                          .toLowerCase()
                          .trim();

                      const filtered =
                        feeGenerations.filter(
                          (s) =>
                            !term ||
                            (
                              s.candidate_name ||
                              ''
                            )
                              .toLowerCase()
                              .includes(
                                term
                              ) ||
                            (
                              s.admission_no ||
                              ''
                            )
                              .toLowerCase()
                              .includes(
                                term
                              ) ||
                            (
                              s.fee_type ||
                              ''
                            )
                              .toLowerCase()
                              .includes(
                                term
                              )
                        );

                      if (
                        filtered.length ===
                        0
                      ) {
                        return (
                          <TableRow>
                            <TableCell
                              colSpan={
                                8
                              }
                              align="center"
                              sx={{
                                py: 5,
                              }}
                            >
                              No records
                              found
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return filtered.map(
                        (s, index) => {
                          const paid =
                            Number(
                              s.total_paid ||
                                0
                            );

                          const total =
                            Number(
                              s.amount ||
                                0
                            );

                          const pending =
                            Number(
                              s.pending_amount ||
                                0
                            );

                          const isFullyPaid =
                            pending <=
                            0;

                          const selectStudent =
                            () => {
                              setSelectedFeeGeneration(
                                s
                              );

                              setCandidateNameDisplay(
                                `${s.candidate_name || 'Student'} — (${s.fee_type})`
                              );

                              setCandidateSearchTerm(
                                ''
                              );

                              setShowCandidateModal(
                                false
                              );
                            };

                          return (
                            <TableRow
                              key={
                                s.fee_id
                              }
                              hover
                              sx={{
                                cursor:
                                  'pointer',
                                opacity:
                                  isFullyPaid
                                    ? 0.7
                                    : 1,
                              }}
                              onClick={
                                selectStudent
                              }
                            >
                              <TableCell>
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                {
                                  s.admission_no
                                }
                              </TableCell>

                              <TableCell>
                                {
                                  s.candidate_name
                                }
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={
                                    s.fee_type ||
                                    '—'
                                  }
                                  size="small"
                                />
                              </TableCell>

                              <TableCell>
                                ₹
                                {total.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )}
                              </TableCell>

                              <TableCell>
                                ₹
                                {paid.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )}
                              </TableCell>

                              <TableCell>
                                ₹
                                {pending.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )}
                              </TableCell>

                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={
                                    isFullyPaid
                                  }
                                  onClick={(
                                    e
                                  ) => {
                                    e.stopPropagation();
                                    selectStudent();
                                  }}
                                >
                                  {isFullyPaid
                                    ? 'Fully Paid'
                                    : 'Select'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      );
                    })()
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              p: 2,
              textAlign:
                'right',
            }}
          >
            <Button
              variant="outlined"
              onClick={() =>
                setShowCandidateModal(
                  false
                )
              }
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Deposit History Modal */}
      <Modal
        open={
          depositHistoryModal
        }
        onClose={() => {
          setDepositHistoryModal(
            false
          );

          setDepositHistoryInstallment(
            null
          );
        }}
      >
        <Box
          sx={{
            ...modalBox(
              560
            ),
            maxHeight:
              '80vh',
            display:
              'flex',
            flexDirection:
              'column',
          }}
        >
          <Box sx={modalHeader}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Deposit History
              </Typography>

              {depositHistoryInstallment && (
                <Typography
                  sx={{
                    color:
                      'rgba(255,255,255,0.75)',
                    fontSize:
                      '0.78rem',
                  }}
                >
                  {isMonthlyFee
                    ? getMonthLabel(
                        depositHistoryInstallment.due_date,
                        depositHistoryInstallment.installment_no
                      )
                    : `Installment ${depositHistoryInstallment.installment_no}`}
                  {' · '}
                  Due:{' '}
                  {fmtDate(
                    depositHistoryInstallment.due_date
                  )}
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={() => {
                setDepositHistoryModal(
                  false
                );

                setDepositHistoryInstallment(
                  null
                );
              }}
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY:
                'auto',
            }}
          >
            <TableContainer>
              <Table
                size="small"
              >
                <TableHead>
                  <TableRow>
                    {[
                      '#',
                      'Deposit ID',
                      'Date',
                      'Mode',
                      'Ref. No',
                      'Amount',
                      'Actions',
                    ].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            color:
                              'primary.main',
                          }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {depositHistoryInstallment?.deposits?.length >
                  0 ? (
                    depositHistoryInstallment.deposits.map(
                      (
                        dep,
                        idx
                      ) => (
                        <TableRow
                          key={
                            dep.deposit_id
                          }
                        >
                          <TableCell>
                            {idx +
                              1}
                          </TableCell>

                          <TableCell>
                            #
                            {
                              dep.deposit_id
                            }
                          </TableCell>

                          <TableCell>
                            {fmtDate(
                              dep.payment_date
                            )}
                          </TableCell>

                          <TableCell>
                            {
                              dep.payment_mode
                            }
                          </TableCell>

                          <TableCell>
                            {
                              dep.reference_no ||
                              '—'
                            }
                          </TableCell>

                          <TableCell>
                            ₹
                            {Number(
                              dep.paid_amount ||
                                0
                            ).toLocaleString(
                              'en-IN',
                              {
                                minimumFractionDigits: 2,
                              }
                            )}
                          </TableCell>

                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handlePrintReceipt(
                                  {
                                    ...dep,
                                    id: dep.deposit_id,
                                    student_name:
                                      feeInstallmentData?.candidate_name,
                                    admission_no:
                                      selectedFeeGeneration?.admission_no,
                                    installment_no:
                                      depositHistoryInstallment.installment_no,
                                    payment_mode_name:
                                      dep.payment_mode,
                                  }
                                )
                              }
                            >
                              <PrintIcon
                                sx={{
                                  fontSize: 15,
                                }}
                              />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => {
                                setDepositToDelete(
                                  dep.deposit_id
                                );

                                setDepositHistoryModal(
                                  false
                                );

                                setDeleteModalOpen(
                                  true
                                );
                              }}
                            >
                              <DeleteIcon
                                sx={{
                                  fontSize: 15,
                                }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          py: 4,
                        }}
                      >
                        No deposits found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={
          editModalOpen
        }
        onClose={() =>
          setEditModalOpen(
            false
          )
        }
      >
        <Box
          sx={modalBox(
            500
          )}
        >
          <Box sx={modalHeader}>
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Edit Deposit #
              {
                editDeposit?.id
              }
            </Typography>

            <IconButton
              onClick={() =>
                setEditModalOpen(
                  false
                )
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 3,
              display:
                'flex',
              flexDirection:
                'column',
              gap: 2,
            }}
          >
            <TextField
              label="Paid Amount *"
              type="number"
              fullWidth
              size="small"
              value={
                editPaidAmount
              }
              onChange={(e) =>
                setEditPaidAmount(
                  e.target.value
                )
              }
              sx={inputSx}
            />

            <FormControl
              fullWidth
              size="small"
            >
              <Select
                value={
                  editPaymentMode
                }
                displayEmpty
                onChange={(e) =>
                  setEditPaymentMode(
                    e.target.value
                  )
                }
                sx={selectSx}
              >
                <MenuItem value="">
                  Select Payment
                  Mode
                </MenuItem>

                {paymentModes.map(
                  (m) => (
                    <MenuItem
                      key={m.id}
                      value={m.id}
                    >
                      {m.name ||
                        m.payment_method}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <TextField
              label="Payment Date *"
              type="date"
              fullWidth
              size="small"
              value={
                editPaymentDate
              }
              onChange={(e) =>
                setEditPaymentDate(
                  e.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={inputSx}
            />

            <Box
              sx={{
                display:
                  'flex',
                justifyContent:
                  'flex-end',
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={() =>
                  setEditModalOpen(
                    false
                  )
                }
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={
                  handleUpdate
                }
                disabled={
                  !editPaidAmount ||
                  !editPaymentMode ||
                  !editPaymentDate ||
                  isSaving
                }
                startIcon={
                  isSaving ? (
                    <CircularProgress
                      size={14}
                      color="inherit"
                    />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {isSaving
                  ? 'Updating…'
                  : 'Update Payment'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={
          showSuccessModal
        }
        onClose={() =>
          setShowSuccessModal(
            false
          )
        }
      >
        <Box
          sx={{
            ...modalBox(
              360
            ),
            p: 4,
            textAlign:
              'center',
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 54,
              color:
                'success.main',
              mb: 1.5,
            }}
          />

          <Typography
            variant="h6"
            fontWeight={700}
          >
            Payment Recorded
          </Typography>

          <Typography
            color="text.disabled"
            sx={{
              mb: 3,
              fontSize:
                '0.9rem',
            }}
          >
            The fee payment
            has been saved
            successfully.
          </Typography>

          <Button
            variant="contained"
            onClick={() =>
              setShowSuccessModal(
                false
              )
            }
          >
            Done
          </Button>
        </Box>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={
          deleteModalOpen
        }
        onClose={() => {
          setDeleteModalOpen(
            false
          );

          setDepositToDelete(
            null
          );
        }}
      >
        <Box
          sx={{
            ...modalBox(
              420
            ),
            p: 4,
          }}
        >
          <Box
            sx={{
              display:
                'flex',
              alignItems:
                'center',
              gap: 1.5,
              mb: 2,
            }}
          >
            <DeleteIcon
              sx={{
                color:
                  'error.main',
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
            >
              Delete Deposit?
            </Typography>
          </Box>

          <Typography
            color="text.disabled"
            sx={{
              mb: 3,
            }}
          >
            This will
            permanently remove
            the deposit record.
            This action cannot
            be undone.
          </Typography>

          <Box
            sx={{
              display:
                'flex',
              gap: 1.5,
              justifyContent:
                'flex-end',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setDeleteModalOpen(
                  false
                );

                setDepositToDelete(
                  null
                );
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={
                confirmDelete
              }
              disabled={
                isDeleting
              }
            >
              {isDeleting
                ? 'Deleting…'
                : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          5000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal:
            'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>

            {/* Receipt Appearance / Print Modal */}
      <Modal
        open={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          },
        }}
      >
        <Box
          sx={{
            ...modalBox(460),
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header follows the chosen primary colour */}
          <Box
            sx={{
              backgroundColor: receiptColorPrimary,
              px: 2.5,
              py: 1.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color 0.2s ease',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.getContrastText(receiptColorPrimary),
                fontWeight: 700,
              }}
            >
              Receipt Appearance
            </Typography>

            <IconButton
              onClick={() => setReceiptModalOpen(false)}
              sx={{ color: theme.palette.getContrastText(receiptColorPrimary) }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflowY: 'auto' }}>
            {[
              {
                title: 'Primary Color',
                caption: 'Header, table headings, amount band',
                value: receiptColorPrimary,
                setter: setReceiptColorPrimary,
              },
              {
                title: 'Accent Color',
                caption: 'Section underlines, highlights',
                value: receiptColorAccent,
                setter: setReceiptColorAccent,
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Box>
                  <Typography fontWeight={600}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.caption}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="input"
                    type="color"
                    value={item.value}
                    onChange={(e) => item.setter(e.target.value)}
                    sx={{
                      width: 48,
                      height: 40,
                      p: 0.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', minWidth: 75 }}
                  >
                    {item.value.toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* PREVIEW */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Preview
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              {receiptLoading ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <CircularProgress size={26} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Loading receipt…
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      backgroundColor: receiptColorPrimary,
                      color: theme.palette.getContrastText(receiptColorPrimary),
                      borderBottom: `4px solid ${receiptColorAccent}`,
                      px: 2,
                      py: 1.2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {receiptBranch.logo ? (
                      <Box
                        component="img"
                        src={receiptBranch.logo}
                        alt="Logo"
                        sx={{
                          width: 36,
                          height: 36,
                          objectFit: 'contain',
                          backgroundColor: '#fff',
                          borderRadius: 1,
                          p: 0.3,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          backgroundColor: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 8,
                          fontWeight: 700,
                          color: receiptColorPrimary,
                        }}
                      >
                        LOGO
                      </Box>
                    )}

                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                        {receiptBranch.name || 'Institute Name'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', opacity: 0.85 }}>
                        {receiptBranch.branch || 'Fee Receipt'}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1 }}
                    >
                      #{receiptData?.receipt_no ?? '—'}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        color: receiptColorPrimary,
                        borderBottom: `2px solid ${receiptColorAccent}`,
                        pb: 0.4,
                        mb: 1,
                      }}
                    >
                      STUDENT DETAILS
                    </Typography>

                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {receiptData?.student?.name || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {receiptData?.student?.admission_no || '—'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: receiptColorPrimary,
                      color: theme.palette.getContrastText(receiptColorPrimary),
                      borderLeft: `6px solid ${receiptColorAccent}`,
                      px: 2,
                      py: 0.8,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>AMOUNT RECEIVED</span>
                    <span>₹ {formatMoney(receiptData?.payment?.paid_amount)}</span>
                  </Box>
                </>
              )}
            </Box>

            {/* ACTIONS */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Button
                variant="text"
                onClick={() => {
                  setReceiptColorPrimary(pdfThemeColor || DEFAULT_RECEIPT_PRIMARY);
                  setReceiptColorText(pdfTextColor);
                  setReceiptColorAccent(DEFAULT_RECEIPT_ACCENT);
                }}
                sx={{ textTransform: 'none' }}
              >
                Reset Colors
              </Button>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={() => setReceiptModalOpen(false)}
                  sx={{ textTransform: 'none' }}
                 data-app-cancel="true">
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  disabled={receiptLoading || !receiptData}
                  onClick={() => {
                    setReceiptModalOpen(false);
                    printReceiptDocument();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Print
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

    </Container>
  );
};

export default FeeDeposit;